import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Select, Checkbox, Card, Divider, Tag, Tooltip, Row, Col, Typography, Menu, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, EyeOutlined, SettingOutlined, MoreOutlined, TeamOutlined } from '@ant-design/icons';
import axios from '../../api';
import theme from '../../theme';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Title, Text } = Typography;

const GroupList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingGroup, setEditingGroup] = useState(null);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [permissionsUpdateTrigger, setPermissionsUpdateTrigger] = useState(0);
  const [documentGroups, setDocumentGroups] = useState([]);
  const [permissionModules, setPermissionModules] = useState({});
  const [permissionModulesLoading, setPermissionModulesLoading] = useState(true);
  const navigate = useNavigate();

  // 拉取权限模块配置
  useEffect(() => {
    const fetchPermissionModules = async () => {
      setPermissionModulesLoading(true);
      try {
        const res = await axios.get('/api/permission_modules/');
        setPermissionModules(res.data.modules || {});
      } catch (err) {
        message.error('获取权限模块失败');
        setPermissionModules({});
      } finally {
        setPermissionModulesLoading(false);
      }
    };
    fetchPermissionModules();
  }, []);

  // 获取用户组列表
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/users/groups/');
      console.log('用户组列表响应:', response.data);
      setGroups(response.data);
    } catch (error) {
      console.error('获取用户组列表失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = '获取用户组列表失败';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = '未登录或登录已过期，请重新登录';
        } else if (error.response.status === 403) {
          errorMessage = '没有权限访问此页面';
        } else {
          errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = '网络请求失败，请检查网络连接';
      }
      
      message.error(errorMessage);
      setError(errorMessage);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取文档组列表
  const fetchDocumentGroups = async () => {
    try {
      console.log('开始获取用户可访问的文档组列表...');
      // 使用新的API接口获取用户可访问的文档组
      const response = await axios.get('/api/user-accessible-document-groups/');
      console.log('用户可访问的文档组列表响应:', response.data);
      setDocumentGroups(response.data.results || response.data || []);
    } catch (error) {
      console.error('获取文档组列表失败:', error);
      message.error('获取文档组列表失败: ' + (error.response?.data?.detail || error.message));
      setDocumentGroups([]);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchDocumentGroups();
  }, [fetchGroups]);

  // 处理新增/编辑
  const handleAddEdit = (record = null) => {
    console.log('打开编辑模态框:', record);
    setEditingGroup(record);
    if (record) {
      form.setFieldsValue({
        ...record,
        document_group_ids: record.document_groups?.map(dg => dg.id) || [],
        permissions: record.permissions || {}
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        permissions: {},
        document_group_ids: []
      });
    }
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('提交表单数据:', values);
      
      // 确保权限数据格式正确
      const permissions = values.permissions || {};
      const normalizedPermissions = {};
      
      Object.keys(permissionModules).forEach(moduleKey => {
        const moduleConfig = permissionModules[moduleKey];
        const modulePerms = permissions[moduleKey] || {};
        normalizedPermissions[moduleKey] = {};
        
        // 安全检查：确保 actions 是对象
        if (moduleConfig && moduleConfig.actions && typeof moduleConfig.actions === 'object') {
          Object.keys(moduleConfig.actions).forEach(action => {
            // 确保权限值是布尔类型
            normalizedPermissions[moduleKey][action] = Boolean(modulePerms[action]);
          });
        } else {
          console.warn(`模块 ${moduleKey} 的 actions 配置无效:`, moduleConfig);
        }
      });
      
      const submitData = {
        ...values,
        permissions: normalizedPermissions
      };

      // 处理文档组字段
      if (submitData.document_group_ids === undefined || submitData.document_group_ids.length === 0) {
        delete submitData.document_group_ids;
      }
      
      console.log('提交的权限数据:', submitData);
      
      if (editingGroup) {
        await axios.put(`/api/users/groups/${editingGroup.id}/`, submitData);
        message.success('更新成功');
      } else {
        await axios.post('/api/users/groups/', submitData);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      fetchGroups();
    } catch (error) {
      console.error('提交失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = editingGroup ? '更新失败' : '创建失败';
      if (error.response) {
        errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = '网络请求失败，请检查网络连接';
      }
      
      message.error(errorMessage);
    }
  };

  // 处理删除
  const handleDelete = async (id) => {
    try {
      console.log('删除用户组:', id);
      await axios.delete(`/api/users/groups/${id}/`);
      console.log('删除成功');
      message.success('删除成功');
      fetchGroups();
    } catch (error) {
      console.error('删除失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = '删除失败';
      if (error.response) {
        errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = '网络请求失败，请检查网络连接';
      }
      
      message.error(errorMessage);
    }
  };

  // 处理权限变更
  const handlePermissionChange = useCallback((moduleKey, action, checked) => {
    const currentPermissions = form.getFieldValue('permissions') || {};
    const modulePermissions = { ...(currentPermissions[moduleKey] || {}) };
    modulePermissions[action] = checked;
    const newPermissions = { ...currentPermissions, [moduleKey]: modulePermissions };
    form.setFieldsValue({ permissions: newPermissions });
    setPermissionsUpdateTrigger(prev => prev + 1);
  }, [form]);

  // 处理模块权限变更
  const handleModulePermissionChange = useCallback((moduleKey, checked) => {
    console.log('全选按钮点击:', { moduleKey, checked });
    const currentPermissions = form.getFieldValue('permissions') || {};
    const moduleConfig = permissionModules[moduleKey];
    if (moduleConfig && moduleConfig.actions && typeof moduleConfig.actions === 'object') {
      // 先获取所有action
      const allActions = Object.keys(moduleConfig.actions);
      // 生成新的modulePermissions，全部设为checked
      const modulePermissions = {};
      allActions.forEach(action => {
        modulePermissions[action] = checked;
      });
      // 合并已有的其他模块权限
      const newPermissions = { ...currentPermissions, [moduleKey]: modulePermissions };
      // 关键：强制触发form更新
      form.setFieldsValue({ permissions: { ...newPermissions } });
      setPermissionsUpdateTrigger(prev => prev + 1);
    } else {
      console.warn(`模块 ${moduleKey} 的 actions 配置无效:`, moduleConfig);
    }
  }, [form, permissionModules]);

  // 查看权限详情
  const handleViewPermissions = (group) => {
    setSelectedGroup(group);
    setPermissionModalVisible(true);
  };

  // 复制用户组
  const handleCopyGroup = async (group) => {
    try {
      const copyData = {
        name: `${group.name}_副本`,
        description: group.description,
        permissions: group.permissions || {}
      };
      
      await axios.post('/api/users/groups/', copyData);
      message.success('复制成功');
      fetchGroups();
    } catch (error) {
      console.error('复制失败:', error);
      message.error('复制失败');
    }
  };

  // 渲染权限标签
  const renderPermissionTags = (permissions) => {
    if (!permissions) return <span style={{ color: '#999' }}>无权限</span>;
    const tags = [];
    Object.entries(permissions).forEach(([module, modulePerms]) => {
      const moduleConfig = permissionModules[module];
      if (moduleConfig && modulePerms) {
        const activeActions = Object.entries(modulePerms)
          .filter(([action, hasPermission]) => hasPermission)
          .map(([action]) => moduleConfig.actions[action] || action);
        if (activeActions.length > 0) {
          tags.push(
            <Tag key={module} color="blue">
              {moduleConfig.name}: {activeActions.join(', ')}
            </Tag>
          );
        }
      }
    });
    return tags.length > 0 ? tags : <span style={{ color: '#999' }}>无权限</span>;
  };

  const columns = [
    {
      title: '用户组名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <a style={{ fontWeight: 500 }} onClick={() => navigate(`/user-group/${record.id}/members`)}>{text}</a>
          {record.description && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: '权限配置',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 300,
      render: (permissions) => renderPermissionTags(permissions)
    },
    {
      title: '人数',
      dataIndex: 'user_count',
      key: 'user_count',
      render: (count, record) => (
        <span
          style={{ color: '#6bc200', fontWeight: 600, cursor: 'pointer' }}
          onClick={() => navigate(`/user-group/${record.id}/members`)}
        >
          {count || 0}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => {
        const menu = [
          {
            key: 'edit',
            icon: <EditOutlined />, label: '编辑', onClick: () => handleAddEdit(record)
          },
          {
            key: 'view',
            icon: <EyeOutlined />, label: '查看权限详情', onClick: () => handleViewPermissions(record)
          },
          {
            key: 'copy',
            icon: <CopyOutlined />, label: '复制', onClick: () => handleCopyGroup(record)
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />, label: (
              <Popconfirm
                title="确定要删除这个用户组吗？"
                description="删除后该用户组下的用户将失去相应权限"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <span style={{ color: '#ff4d4f' }}>删除</span>
              </Popconfirm>
            )
          }
        ];
        return (
          <Dropdown menu={{ items: menu }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  if (permissionModulesLoading) {
    return <div>正在加载权限模块...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>用户组管理</Title>
          <Text type="secondary">管理用户组及其权限配置</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleAddEdit()}
          style={{ background: theme.BUTTON_BG, color: '#231a17' }}
        >
          新增用户组
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={groups}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      {/* 新增/编辑用户组模态框 */}
      <Modal
        title={editingGroup ? '编辑用户组' : '新增用户组'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        destroyOnHidden
        width={900}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="用户组名称"
                rules={[{ required: true, message: '请输入用户组名称' }]}
              >
                <Input placeholder="请输入用户组名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                label="描述"
              >
                <Input placeholder="请输入用户组描述" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>权限配置</Divider>
          
          <Form.Item
            name="permissions"
            label="模块权限"
          >
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {Object.entries(permissionModules).map(([moduleKey, moduleConfig]) => {
                const permissions = form.getFieldValue('permissions') || {};
                const modulePerms = permissions[moduleKey] || {};
                
                // 确保权限值是布尔类型
                const normalizedModulePerms = {};
                if (moduleConfig.actions && typeof moduleConfig.actions === 'object') {
                  Object.keys(moduleConfig.actions).forEach(action => {
                    normalizedModulePerms[action] = Boolean(modulePerms[action]);
                  });
                }
                
                const allChecked = moduleConfig.actions && typeof moduleConfig.actions === 'object'
                  ? Object.keys(moduleConfig.actions).every(action => normalizedModulePerms[action])
                  : false;
                const someChecked = moduleConfig.actions && typeof moduleConfig.actions === 'object'
                  ? Object.keys(moduleConfig.actions).some(action => normalizedModulePerms[action])
                  : false;
                
                // 添加 permissionsUpdateTrigger 依赖，确保状态更新时重新渲染
                const _ = permissionsUpdateTrigger;
                
                // 调试信息
                console.log(`模块 ${moduleKey} 权限状态:`, {
                  originalModulePerms: modulePerms,
                  normalizedModulePerms,
                  allChecked,
                  someChecked,
                  actions: moduleConfig.actions
                });
                
                return (
                  <Card
                    key={moduleKey}
                    size="small"
                    style={{ marginBottom: 16 }}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{moduleConfig.icon}</span>
                        <span>{moduleConfig.name}</span>
                        <Checkbox
                          key={`${moduleKey}-select-all`}
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleModulePermissionChange(moduleKey, e.target.checked);
                          }}
                        >
                          全选
                        </Checkbox>
                      </div>
                    }
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      {moduleConfig.actions && typeof moduleConfig.actions === 'object' ? 
                        Object.entries(moduleConfig.actions).map(([action, actionName]) => (
                          <Checkbox
                            key={`${moduleKey}-${action}`}
                            checked={normalizedModulePerms[action]}
                            onChange={(e) => {
                              e.stopPropagation();
                              handlePermissionChange(moduleKey, action, e.target.checked);
                            }}
                          >
                            {actionName}
                          </Checkbox>
                        ))
                        : <div style={{ color: '#999' }}>权限配置无效</div>
                      }
                    </div>
                  </Card>
                );
              })}
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限详情查看模态框 */}
      <Modal
        title={`权限详情 - ${selectedGroup?.name}`}
        open={permissionModalVisible}
        onCancel={() => setPermissionModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedGroup && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>用户组描述：</Text>
              <div style={{ marginTop: 8, color: '#666' }}>
                {selectedGroup.description || '暂无描述'}
              </div>
            </div>
            
            <Divider>权限配置详情</Divider>
            
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {Object.entries(permissionModules).map(([moduleKey, moduleConfig]) => {
                const modulePerms = selectedGroup.permissions?.[moduleKey] || {};
                const hasAnyPermission = Object.values(modulePerms).some(Boolean);
                
                return (
                  <Card
                    key={moduleKey}
                    size="small"
                    style={{ marginBottom: 12 }}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{moduleConfig.icon}</span>
                        <span>{moduleConfig.name}</span>
                        {hasAnyPermission && <Tag color="green">有权限</Tag>}
                      </div>
                    }
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {moduleConfig.actions && typeof moduleConfig.actions === 'object' ? 
                        Object.entries(moduleConfig.actions).map(([action, actionName]) => {
                          const hasPermission = modulePerms[action] || false;
                          
                          return (
                            <Tag
                              key={action}
                              color={hasPermission ? 'blue' : 'default'}
                            >
                              {actionName}
                            </Tag>
                          );
                        })
                        : <div style={{ color: '#999' }}>权限配置无效</div>
                      }
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GroupList; 